import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { collectionService, memberService } from "@/services";
import type { CollectionDemand, Member, ReceiptView } from "@/services/types";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { PageHeader, BreadcrumbItem } from "@/components/feature/PageHeader";
import { Card } from "@/components/base/Card";
import { Button } from "@/components/base/Button";
import { Checkbox } from "@/components/base/Checkbox";
import { TextField } from "@/components/base/TextField";
import { EmptyState } from "@/components/base/EmptyState";
import { CurrencyInput } from "@/components/currency/CurrencyInput";
import { CurrencyDisplay } from "@/components/currency/CurrencyDisplay";
import { Badge } from "@/components/base/Badge";
import { formatINRNumber } from "@/utils/format";
import { PaymentForm } from "../components/PaymentForm";
import { demandStatusMeta } from "../components/labels";
import { PreviousDuesAlertCard } from "../components/PreviousDuesAlertCard";
import type { PaymentMode } from "@/services/types";

type Selections = Record<string, number>;

export default function ReceiptEntryPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();
  const actor = user?.name ?? "Staff User";

  const [query, setQuery] = useState("");
  const [results, setResults] = useState<Member[]>([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [selected, setSelected] = useState<Member | null>(null);
  const [demands, setDemands] = useState<CollectionDemand[]>([]);
  const [selections, setSelections] = useState<Selections>({});
  const [lateFeeSelections, setLateFeeSelections] = useState<Record<string, boolean>>({});
  const [successReceipts, setSuccessReceipts] = useState<ReceiptView[] | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const runSearch = useCallback(async (q: string) => {
    if (!q.trim()) {
      setResults([]);
      return;
    }
    try {
      setResults(await memberService.searchMembers(q));
    } catch {
      setResults([]);
    }
  }, []);

  useEffect(() => {
    const t = window.setTimeout(() => runSearch(query), 250);
    return () => window.clearTimeout(t);
  }, [query, runSearch]);

  const pickMember = async (member: Member) => {
    setSelected(member);
    setQuery("");
    setResults([]);
    setSearchOpen(false);
    setSuccessReceipts(null);
    const pending = await collectionService.getMemberPendingDemands(member.id);
    setDemands(pending);
    const init: Selections = {};
    const initLate: Record<string, boolean> = {};
    for (const d of pending) {
      const baseDue = Math.max(0, d.balance - Math.max(0, d.lateFee || 0));
      initLate[d.id] = false;
      init[d.id] = baseDue;
    }
    setSelections(init);
    setLateFeeSelections(initLate);
  };

  const reset = () => {
    setSelected(null);
    setDemands([]);
    setSelections({});
    setLateFeeSelections({});
    setSuccessReceipts(null);
    setQuery("");
    searchRef.current?.focus();
  };

  const totalSelected = useMemo(
    () => demands.reduce((sum, d) => sum + (selections[d.id] ?? 0), 0),
    [demands, selections]
  );

  const submit = async (values: { mode: PaymentMode; reference: string; receivedOn: string; remarks: string }) => {
    if (!selected) return false;
    const payments = demands
      .filter((d) => (selections[d.id] ?? 0) > 0)
      .map((d) => ({ demandId: d.id, amount: selections[d.id] as number, groupMemberId: d.groupMemberId }));
    if (payments.length === 0) {
      toast.error("Nothing selected", "Select at least one demand and enter an amount.");
      return false;
    }

    const applyLateFeeIds = demands
      .filter((d) => {
        const fee = Math.max(0, d.lateFee > 0 ? d.lateFee : (d.applicableLateFee ?? 0));
        return fee > 0 && (lateFeeSelections[d.id] ?? false);
      })
      .map((d) => d.id);

    try {
      // A cross-group selection becomes one receipt per group in real-API
      // mode (a receipt is scoped to one group_member there) — mock mode
      // always returns exactly one.
      const { receipts } = await collectionService.recordPayment({
        memberId: selected.id,
        payments,
        mode: values.mode,
        reference: values.reference,
        receivedOn: values.receivedOn,
        remarks: values.remarks,
        actor,
        applyLateFee: applyLateFeeIds,
      });
      setSuccessReceipts(receipts);
      const total = receipts.reduce((s, r) => s + r.total, 0);
      toast.success(
        "Payment recorded",
        receipts.length === 1
          ? `${receipts[0].receiptNumber} issued for ${formatINRNumber(receipts[0].total)}.`
          : `${receipts.length} receipts issued (${receipts.map((r) => r.receiptNumber).join(", ")}) for ${formatINRNumber(total)}.`
      );
      return true;
    } catch (err) {
      toast.error("Could not record payment", err instanceof Error ? err.message : "Please try again.");
      return false;
    }
  };

  const setAmount = (demandId: string, value: number | null) => {
    setSelections((prev) => ({ ...prev, [demandId]: value ?? 0 }));
  };

  const toggleDemandLateFee = (demandId: string, checked: boolean) => {
    setLateFeeSelections((prev) => ({ ...prev, [demandId]: checked }));
    const d = demands.find((item) => item.id === demandId);
    if (d) {
      const bDue = Math.max(0, d.balance - Math.max(0, d.lateFee || 0));
      const fAmount = Math.max(0, d.lateFee > 0 ? d.lateFee : (d.applicableLateFee ?? 0));
      const newPayable = checked ? bDue + fAmount : bDue;
      if ((selections[demandId] ?? 0) > 0) {
        setSelections((prev) => ({ ...prev, [demandId]: newPayable }));
      }
    }
  };

  const selectedCount = demands.filter((d) => (selections[d.id] ?? 0) > 0).length;

  return (
    <div>
      <PageHeader
        breadcrumb={
          <>
            <BreadcrumbItem label="Home" to="/" />
            <BreadcrumbItem label="Collection" to="/collection" />
            <BreadcrumbItem label="Record receipt" isLast />
          </>
        }
        title="Record receipt"
        subtitle="Search a member to see everything they owe across all groups"
        actions={
          <Button variant="outline" size="sm" icon={<i className="ri-arrow-left-line" />} onClick={() => navigate("/collection")}>
            Back
          </Button>
        }
      />

      {successReceipts ? (
        <Card>
          <div className="flex flex-col items-center gap-3 py-6 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-success-100 text-success-600">
              <i className="ri-check-double-line text-2xl" />
            </div>
            <h3 className="text-base font-semibold text-foreground-950">Payment recorded</h3>
            {successReceipts.length === 1 ? (
              <p className="text-sm text-foreground-600">
                Receipt <span className="tnum font-semibold text-foreground-900">{successReceipts[0].receiptNumber}</span> issued.
              </p>
            ) : (
              <p className="text-sm text-foreground-600">
                {successReceipts.length} receipts issued — one per group.
              </p>
            )}
            <div className="flex flex-wrap justify-center gap-2">
              {successReceipts.map((r) => (
                <Button
                  key={r.id}
                  size="sm"
                  variant="outline"
                  icon={<i className="ri-eye-line" />}
                  onClick={() => navigate(`/collection/receipts/${r.id}`)}
                >
                  {successReceipts.length === 1 ? "View / print receipt" : `View ${r.receiptNumber}`}
                </Button>
              ))}
              <Button size="sm" icon={<i className="ri-user-add-line" />} onClick={reset}>
                Record another
              </Button>
            </div>
          </div>
        </Card>
      ) : !selected ? (
        <Card
          title="Find member"
          subtitle="Type a name fragment, phone number or member code — partial matches are accepted"
        >
          <div className="relative">
            <TextField
              ref={searchRef}
              placeholder="Search name, phone or member code…"
              leftIcon="ri-search-line"
              value={query}
              autoFocus
              onChange={(e) => {
                setQuery(e.target.value);
                setSearchOpen(true);
              }}
              onFocus={() => setSearchOpen(true)}
            />
            {searchOpen && results.length > 0 ? (
              <div className="absolute z-30 mt-1 w-full overflow-hidden rounded-md border border-background-200 bg-background-50 shadow-lg shadow-foreground-950/5">
                {results.map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => pickMember(m)}
                    className="flex w-full cursor-pointer items-center justify-between px-3 py-2.5 text-left hover:bg-background-100"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-foreground-900">{m.fullName}</p>
                      <p className="tnum text-xs text-foreground-500">
                        {m.memberCode} · {m.city}
                      </p>
                    </div>
                    <span className="tnum flex-none text-xs text-foreground-400">{m.primaryPhone}</span>
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="space-y-4 lg:col-span-2">
            <Card
              title={selected.fullName}
              subtitle={`${selected.memberCode} · ${selected.primaryPhone} · ${selected.city}`}
              actions={
                <Button size="sm" variant="ghost" icon={<i className="ri-swap-line" />} onClick={reset}>
                  Change member
                </Button>
              }
            >
              {demands.length === 0 ? (
                <EmptyState
                  icon="ri-check-double-line"
                  title="All caught up"
                  description="This member has no outstanding balances across their groups."
                />
              ) : (
                <div className="space-y-3">
                  {demands.length > 1 ? (
                    <PreviousDuesAlertCard
                      memberName={selected.fullName}
                      previousDues={demands.slice(0, demands.length - 1).map((d) => ({
                        installmentId: d.id,
                        cycleId: d.id,
                        cycleNo: d.cycle,
                        periodLabel: d.periodLabel,
                        baseAmount: d.baseInstallment,
                        lateFee: d.lateFee,
                        netPayable: d.netPayable,
                        amountPaid: d.paid,
                        balance: d.balance,
                        dueDate: d.dueDate,
                        status: d.status,
                      }))}
                    />
                  ) : null}
                  <p className="text-xs text-foreground-500">
                    Pending demands across {new Set(demands.map((d) => d.groupName)).size} group(s), oldest due first
                  </p>
                  {demands.map((d) => {
                    const baseDue = Math.max(0, d.balance - Math.max(0, d.lateFee || 0));
                    const feeAmount = Math.max(0, d.lateFee > 0 ? d.lateFee : (d.applicableLateFee ?? 0));
                    const hasLateFee = feeAmount > 0;
                    const applyFee = hasLateFee ? (lateFeeSelections[d.id] ?? false) : false;
                    const totalPayable = applyFee ? baseDue + feeAmount : baseDue;
                    const amount = selections[d.id] ?? 0;
                    const meta = demandStatusMeta(d.status, d.daysOverdue > 0);
                    return (
                      <div key={d.id} className="rounded-lg border border-background-200 p-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="text-sm font-medium text-foreground-900">{d.groupName}</span>
                              <Badge variant="active">Cycle {d.cycle}</Badge>
                              <Badge variant={meta.badge}>{meta.label}</Badge>
                            </div>
                            <p className="tnum mt-0.5 text-xs text-foreground-500">
                              {d.periodLabel} · net {formatINRNumber(d.netPayable)} · base balance{" "}
                              <span className="font-medium text-foreground-700">{formatINRNumber(baseDue)}</span>
                              {hasLateFee ? (
                                applyFee ? (
                                  <span className="ml-1 font-medium text-amber-600">
                                    (+{formatINRNumber(feeAmount)} fee applied = {formatINRNumber(totalPayable)})
                                  </span>
                                ) : (
                                  <span className="ml-1 font-medium text-success-600">
                                    (fee excluded = {formatINRNumber(baseDue)})
                                  </span>
                                )
                              ) : null}
                            </p>
                          </div>
                          <div className="flex flex-none items-center gap-2">
                            <CurrencyInput
                              value={amount}
                              onChange={(v) => setAmount(d.id, v)}
                              placeholder="0.00"
                            />
                            <button
                              type="button"
                              onClick={() => setAmount(d.id, totalPayable)}
                              className="cursor-pointer whitespace-nowrap text-xs font-medium text-primary-600 hover:underline"
                            >
                              Full
                            </button>
                          </div>
                        </div>

                        {hasLateFee && (
                          <div className="mt-2 flex items-center justify-between gap-2 rounded-md bg-amber-50/80 px-2.5 py-1.5 text-xs border border-amber-200">
                            <div className="flex items-center gap-1.5">
                              <span className="font-semibold text-amber-900">
                                Late fee: {formatINRNumber(feeAmount)}
                              </span>
                              <span
                                className={`inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                                  applyFee ? "bg-amber-200/70 text-amber-900" : "bg-foreground-200/60 text-foreground-700"
                                }`}
                              >
                                {applyFee ? "APPLIED IN DUE" : "NOT APPLIED"}
                              </span>
                            </div>
                            <label className="flex items-center gap-1.5 cursor-pointer select-none font-medium text-amber-900">
                              <input
                                type="checkbox"
                                checked={applyFee}
                                onChange={(e) => toggleDemandLateFee(d.id, e.target.checked)}
                                className="h-3.5 w-3.5 rounded border-amber-300 text-primary-600 focus:ring-primary-500 cursor-pointer"
                              />
                              <span>Apply late fee</span>
                            </label>
                          </div>
                        )}

                        <div className="mt-2 border-t border-background-100 pt-2">
                          <Checkbox
                            checked={amount > 0}
                            onChange={(checked) => setAmount(d.id, checked ? totalPayable : 0)}
                            label={`Include in this payment (${formatINRNumber(totalPayable)})`}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          </div>

          <div className="space-y-4">
            <Card title="Payment details">
              <div className="mb-3 rounded-md bg-background-100/60 px-3 py-2.5">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-foreground-500">Selected demands</span>
                  <span className="tnum font-medium text-foreground-900">{selectedCount}</span>
                </div>
                <div className="mt-1 flex items-center justify-between">
                  <span className="text-foreground-500">Total</span>
                  <span className="tnum text-lg font-semibold text-foreground-950">
                    <CurrencyDisplay amount={totalSelected} hideSymbol />
                  </span>
                </div>
              </div>
              <PaymentForm
                onSubmit={submit}
                submitLabel="Record payment"
                submitIcon="ri-check-double-line"
              />
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}